import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories } from '@/mocks/products';

function CategoryCarousel({ categoryKey, categoryUrl, products }: {
  categoryKey: string;
  categoryUrl: string;
  products: { id: string; title: string; price: string; image: string; link: string }[];
}) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = trackRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  return (
    <div className="mb-10 md:mb-14">
      <div className="flex items-center justify-between mb-4 md:mb-5 px-4 md:px-8 lg:px-12">
        <h3 className="font-heading text-lg md:text-xl lg:text-2xl font-bold text-foreground-900">
          {t(categoryKey)}
        </h3>
        <a
          href={categoryUrl}
          target="_blank"
          rel="noopener nofollow"
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
        >
          {t('products.view_all')}
          <i className="ri-arrow-right-s-line" />
        </a>
      </div>

      <div className="relative px-4 md:px-8 lg:px-12">
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-md border border-background-200 flex items-center justify-center text-foreground-700 hover:text-primary-600 hover:shadow-lg transition-all cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl" />
          </button>
        )}

        <div
          ref={trackRef}
          onScroll={checkScroll}
          className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <a
              key={product.id}
              href={product.link}
              target="_blank"
              rel="noopener nofollow"
              className="group flex-shrink-0 snap-start w-40 sm:w-44 md:w-52 lg:w-56 bg-white rounded-xl border border-background-200 hover:border-primary-300 overflow-hidden transition-all duration-300 hover:shadow-md cursor-pointer"
            >
              <div className="relative w-full h-40 sm:h-44 md:h-52 lg:h-56 overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-2.5 md:p-3">
                <p className="text-xs md:text-sm text-foreground-700 leading-snug line-clamp-2 mb-1.5 min-h-[2.5em]">
                  {product.title}
                </p>
                <p className="text-xs md:text-sm font-bold text-primary-600">
                  {product.price}
                </p>
              </div>
            </a>
          ))}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white shadow-md border border-background-200 flex items-center justify-center text-foreground-700 hover:text-primary-600 hover:shadow-lg transition-all cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ProductShowcase() {
  const { t } = useTranslation();

  return (
    <section className="py-12 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12 px-4 md:px-8">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
            {t('products.title')}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            {t('products.subtitle')}
          </p>
        </div>

        {productCategories.map((cat) => (
          <CategoryCarousel
            key={cat.key}
            categoryKey={cat.key}
            categoryUrl={cat.categoryUrl}
            products={cat.products}
          />
        ))}
      </div>
    </section>
  );
}